import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import type { Payee } from "../types";
import { api } from "../lib/api";
import { shouldDeferCreate } from "../lib/offline-associated";
import PayeeAvatar from "./PayeeAvatar";
import { nameKey } from "../lib/names";

export default function PayeeField({
  payees,
  value,
  onChange,
  onCreated,
  placeholder = "Search or add a payee",
  ariaLabel,
  disabled,
  pendingName,
  onDraftChange,
  /** When false, new payees require a live server create (loans/bills need a real id immediately). */
  deferCreate = true,
}: {
  payees: Payee[];
  value: string;
  /** `payeeName` is set for a deferred offline create with no server id yet. */
  onChange: (payeeId: string, payeeName?: string) => void;
  onCreated?: (payee: Payee) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  // A payee name proposed (e.g. by Moo/Smart Add) with no payeeId yet, because it doesn't exist
  // as a payee record until this entry is actually saved. Shown as the starting text so the field
  // doesn't render blank and look like the proposal was dropped; typing still overrides it normally.
  pendingName?: string;
  /** Keep typing local until the parent saves the transaction and resolves its payee. */
  onDraftChange?: (name: string) => void;
  deferCreate?: boolean;
}) {
  // Payees created in this session that the `payees` prop (fetched once by the parent) doesn't
  // know about yet, so a just-added payee shows up in the list immediately, not after a refresh.
  const [localPayees, setLocalPayees] = useState<Payee[]>([]);
  const all = [...payees, ...localPayees.filter((lp) => !payees.some((p) => p.id === lp.id))];
  const active = all.filter((p) => p.active !== false);
  const selected = all.find((p) => p.id === value);
  const [query, setQuery] = useState(selected?.name || pendingName || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const match = all.find((p) => p.id === value);
    setQuery(match?.name || pendingName || "");
    // Only re-sync when the committed selection changes externally, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, pendingName]);

  // Compared the way the server compares payee names, so "corner  shop" picks the Corner Shop rather
  // than offering to create one the server would then refuse as a duplicate.
  const q = nameKey(query);
  const matches = q ? active.filter((p) => nameKey(p.name).includes(q)) : active;
  const exactMatch = active.find((p) => nameKey(p.name) === q);

  function acceptDeferred(name: string) {
    const trimmed = name.trim();
    const local: Payee = {
      id: `local:${trimmed}`,
      name: trimmed,
      type: "Person",
      notes: "",
      active: true,
    };
    setLocalPayees((v) => [...v.filter((p) => p.id !== local.id), local]);
    onChange(local.id, trimmed);
    setQuery(trimmed);
    onCreated?.(local);
    setOpen(false);
  }

  async function createPayee(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (deferCreate && typeof navigator !== "undefined" && !navigator.onLine) {
      acceptDeferred(trimmed);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await api<{ payee: Payee }>("payees/save", { name: trimmed, type: "Person", notes: "" });
      setLocalPayees((v) => [...v, result.payee]);
      onChange(result.payee.id);
      setQuery(result.payee.name);
      onCreated?.(result.payee);
      setOpen(false);
    } catch (e) {
      if (deferCreate && shouldDeferCreate(e)) {
        acceptDeferred(trimmed);
        return;
      }
      setError(
        !deferCreate && shouldDeferCreate(e)
          ? "Connect to add a new payee."
          : e instanceof Error ? e.message : "Could not add this payee.",
      );
    } finally {
      setBusy(false);
    }
  }

  function select(payee: Payee) {
    onChange(payee.id.startsWith("local:") ? payee.id : payee.id, payee.id.startsWith("local:") ? payee.name : undefined);
    setQuery(payee.name);
    setOpen(false);
  }

  return (
    <span className="payee-field">
      {(selected || pendingName) && (
        <PayeeAvatar id={selected?.id || pendingName || "pending"} name={selected?.name || pendingName || ""} size={26} />
      )}
      <span className="payee-field-input">
        <input
          aria-label={ariaLabel}
          value={query}
          disabled={disabled || busy}
          placeholder={placeholder}
          autoComplete="off"
          maxLength={onDraftChange ? 1000 : 160}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setError("");
            setOpen(true);
            if (onDraftChange) onDraftChange(e.target.value);
            else if (!e.target.value) onChange("");
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && open) {
              // Closes the suggestions only; a second Escape closes the sheet as usual.
              e.preventDefault();
              setOpen(false);
            }
            if (e.key === "Enter") {
              e.preventDefault();
              if (onDraftChange) setOpen(false);
              else if (exactMatch) select(exactMatch);
              else if (q) void createPayee(query.trim());
            }
          }}
        />
        {open && !disabled && (matches.length > 0 || (!onDraftChange && q)) && (
          <div className="payee-field-menu">
            {matches.map((p) => (
              <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => select(p)}>
                <PayeeAvatar id={p.id} name={p.name} size={22} />
                {p.name}
                {p.id.startsWith("local:") ? " (on this device)" : ""}
              </button>
            ))}
            {q && !exactMatch && !onDraftChange && (
              <button
                type="button"
                className="payee-field-add"
                disabled={busy || query.trim().length > 160}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void createPayee(query.trim())}
              >
                <Plus size={14} /> Add "{query.trim()}"
              </button>
            )}
          </div>
        )}
      </span>
      {busy && <small>Adding payee…</small>}
      {error && (
        <small className="form-error" role="alert">
          {error}
        </small>
      )}
    </span>
  );
}
